import { Vocabulary } from "../../Utils/Vocabulary";
import { 
  CheckCircleRounded, 
  Cancel, 
  RadioButtonUnchecked,
  Quiz
} from "@mui/icons-material";
import { 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemIcon, 
  ListItemText,
  Paper,
  Chip
} from "@mui/material";
import { useEffect, useRef } from "react";

type Props = {
  questions: any;
  onSelect: (index: number) => void;
  currentIndex: number;
};

const getStatusConfig = (status: string, isActive: boolean) => {
  switch (status) {
    case "correct":
      return {
        icon: <CheckCircleRounded />,
        color: "success" as const,
        bgColor: isActive ? "success.light" : "success.lighter",
        textColor: isActive ? "success.contrastText" : "success.main"
      };
    case "incorrect":
      return {
        icon: <Cancel />,
        color: "error" as const,
        bgColor: isActive ? "error.light" : "error.lighter", 
        textColor: isActive ? "error.contrastText" : "error.main"
      };
    default:
      return {
        icon: <RadioButtonUnchecked />,
        color: "default" as const,
        bgColor: isActive ? "primary.light" : "grey.50",
        textColor: isActive ? "primary.contrastText" : "text.secondary"
      };
  }
};

export default function QuestionSummary({
  questions,
  onSelect,
  currentIndex
}: Props) {

  const getStatus = (q: any): string => {
    const progress = q.progress;
    if (!progress) return "unanswered";
    return progress?.isCorrect ? "correct" : "incorrect";
  };

  const currentItemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (currentItemRef.current) {
      currentItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [currentIndex]);

  // Calculate statistics
  const stats = questions.reduce(
    (acc: any, q: any) => {
      const status = getStatus(q);
      acc[status]++;
      return acc;
    },
    { correct: 0, incorrect: 0, unanswered: 0 }
  );

  const handleQuestionSelect = (index: number) => {
    onSelect(index);
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "280px", md: "300px", lg: "320px" },
        height: "calc(100vh - 80px)",
        flexShrink: 0
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden"
        }}
      >
      {/* Header with title and stats */}
      <Box
        sx={{
          p: 2,
          background: "linear-gradient(135deg, primary.main 0%, primary.dark 100%)",
          color: "primary.contrastText"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Quiz sx={{ mr: 1, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {Vocabulary.questionsSummary}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            size="small"
            label={`${stats.correct} ✓`}
            sx={{ 
              bgcolor: "success.main", 
              color: "success.contrastText",
              fontSize: "0.75rem"
            }}
          />
          <Chip
            size="small"
            label={`${stats.incorrect} ✗`}
            sx={{ 
              bgcolor: "error.main", 
              color: "error.contrastText",
              fontSize: "0.75rem"
            }}
          />
          <Chip
            size="small"
            label={`${stats.unanswered} ○`}
            sx={{ 
              bgcolor: "grey.400", 
              color: "white",
              fontSize: "0.75rem"
            }}
          />
        </Box>
      </Box>

      {/* Question List */}
      <List
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: 0,
          width: "100%"
        }}
      >
        {questions.map((q: any, index: number) => {
          const status = getStatus(q);
          const isActive = index === currentIndex;
          const statusConfig = getStatusConfig(status, isActive);

          return (
            <ListItem key={q._id} disablePadding>
              <ListItemButton
                onClick={() => handleQuestionSelect(index)}
                ref={isActive ? currentItemRef : null}
                sx={{
                  backgroundColor: isActive ? "primary.light" : "transparent",
                  borderLeft: isActive ? "4px solid" : "4px solid transparent",
                  borderLeftColor: isActive ? "primary.main" : "transparent",
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: isActive ? "primary.light" : "action.hover"
                  },
                  py: 1.5,
                  width: "100%",
                  boxSizing: "border-box"
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box
                    sx={{
                      color: statusConfig.color === "default" 
                        ? (isActive ? "primary.main" : "text.disabled")
                        : `${statusConfig.color}.main`,
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    {statusConfig.icon}
                  </Box>
                </ListItemIcon>
                
                <ListItemText
                  primary={`${Vocabulary.question} ${index + 1}`}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "primary.contrastText" : "text.primary",
                    fontSize: "0.95rem"
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Paper>
    </Box>
  );
}
